import { useMemo } from 'react';
import { useScript } from 'usehooks-ts';
import type { UseScriptStatus } from 'usehooks-ts';
import { useCustomPreferences } from '../../utils/api';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { RECAPTCHA_CUSTOM_PREFERENCES } from '../recaptcha';

export const RECAPTCHA_ENTERPRISE_SCRIPT_URL = 'https://www.google.com/recaptcha/enterprise.js';

export interface UseRecaptchaScriptLoaderResult {
    isRecaptchaEnabled: boolean;
    scriptStatus: UseScriptStatus;
}
export const useRecaptchaScriptLoader = (): UseRecaptchaScriptLoaderResult => {
    const preferences = useCustomPreferences(RECAPTCHA_CUSTOM_PREFERENCES);

    const isRecaptchaEnabled = useMemo(() => {
        const recaptchaEnabledPreferences = [
            CustomPreference.RECAPTCHA_ACCOUNT_LOGIN_ENABLED,
            CustomPreference.RECAPTCHA_ACCOUNT_REGISTRATION_ENABLED,
            /* TODO: Uncomment when recaptcha is implemented in these features
            CustomPreference.RECAPTCHA_FORGOT_PASSWORD_ENABLED,
            CustomPreference.RECAPTCHA_PASSWORD_RESET_ENABLED,
            CustomPreference.RECAPTCHA_TRACK_ORDER_ENABLED,
            */
            CustomPreference.RECAPTCHA_GIFT_CARD_BALANCE_ENABLED,
            CustomPreference.RECAPTCHA_PROMO_CODE_ENABLED,
            /* TODO: Uncomment when recaptcha is implemented in these features
            CustomPreference.RECAPTCHA_ADD_PAYMENT_METHOD_ENABLED,
            CustomPreference.RECAPTCHA_PLACE_ORDER_ENABLED,
            */
        ];

        return recaptchaEnabledPreferences.some((pref) => {
            const value = preferences[pref];
            return value === 'true' || value === true;
        });
    }, [preferences]);

    const scriptUrl = useMemo(() => {
        const siteKeyV3 = preferences[CustomPreference.RECAPTCHA_V3_SITE_KEY]
            ? String(preferences[CustomPreference.RECAPTCHA_V3_SITE_KEY])
            : null;

        if (!isRecaptchaEnabled || !siteKeyV3) {
            return null;
        }
        return `${RECAPTCHA_ENTERPRISE_SCRIPT_URL}?render=${siteKeyV3}`;
    }, [isRecaptchaEnabled, preferences]);

    const scriptStatus = useScript(scriptUrl || '', {
        removeOnUnmount: false,
    });

    return {
        isRecaptchaEnabled,
        scriptStatus,
    };
};
